import React, { useState, useMemo, useRef } from "react";
import { BottomSheet } from "../../../shared/components/BottomSheet.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { useInvoices } from "../../invoices/hooks/useInvoices.js";
import { usePurchases } from "../../purchases/hooks/usePurchases.js";
import { useCompanies } from "../../companies/hooks/useCompanies.js";
import { calculateGstReport } from "../services/gstReportService.js";
import { exportGstReportToCsv, getGstDueDates } from "../utils/gstExportUtils.js";
import { downloadGstReportPdf, shareGstReportPdf } from "../utils/gstPdfUtils.js";
import { GstReportTabPanes } from "./GstReportTabPanes.jsx";

export function GstReportModal({ isOpen, onClose }) {
  const { invoices } = useInvoices();
  const { purchases } = usePurchases();
  const { defaultCompany } = useCompanies();

  const [preset, setPreset] = useState("THIS_MONTH");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [activeTab, setActiveTab] = useState("3B"); // '3B' | 'SHEET' | 'B2B' | 'B2C' | 'HSN' | 'PURCHASE'
  const [accountantPhone, setAccountantPhone] = useState(() => localStorage.getItem("accountant_phone") || "");
  const [isExporting, setIsExporting] = useState(false);

  const printableRef = useRef(null);

  const { fromDate, toDate, periodLabel } = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const pad = (n) => String(n).padStart(2, "0");
    const lastDay = (year, month) => new Date(year, month + 1, 0).getDate();

    if (preset === "THIS_MONTH") {
      return { fromDate: `${y}-${pad(m + 1)}-01`, toDate: `${y}-${pad(m + 1)}-${pad(lastDay(y, m))}`, periodLabel: now.toLocaleString("default", { month: "short", year: "numeric" }) };
    }
    if (preset === "LAST_MONTH") {
      const lm = m === 0 ? 11 : m - 1;
      const ly = m === 0 ? y - 1 : y;
      const d = new Date(ly, lm, 1);
      return { fromDate: `${ly}-${pad(lm + 1)}-01`, toDate: `${ly}-${pad(lm + 1)}-${pad(lastDay(ly, lm))}`, periodLabel: d.toLocaleString("default", { month: "short", year: "numeric" }) };
    }
    if (preset === "Q1") return { fromDate: `${y}-04-01`, toDate: `${y}-06-30`, periodLabel: `Q1 (Apr - Jun ${y})` };
    if (preset === "Q2") return { fromDate: `${y}-07-01`, toDate: `${y}-09-30`, periodLabel: `Q2 (Jul - Sep ${y})` };
    if (preset === "Q3") return { fromDate: `${y}-10-01`, toDate: `${y}-12-31`, periodLabel: `Q3 (Oct - Dec ${y})` };
    if (preset === "Q4") return { fromDate: `${y + 1}-01-01`, toDate: `${y + 1}-03-31`, periodLabel: `Q4 (Jan - Mar ${y + 1})` };
    return { fromDate: customFrom, toDate: customTo, periodLabel: `${customFrom || "Start"} to ${customTo || "End"}` };
  }, [preset, customFrom, customTo]);

  const reportData = useMemo(() => {
    return calculateGstReport({ invoices, purchases, fromDate, toDate, companyId: defaultCompany?.id });
  }, [invoices, purchases, fromDate, toDate, defaultCompany]);

  const dueDates = useMemo(() => getGstDueDates(new Date()), []);

  const handlePhoneChange = (e) => {
    setAccountantPhone(e.target.value);
    localStorage.setItem("accountant_phone", e.target.value);
  };

  const handleDownloadCsv = () => {
    exportGstReportToCsv(reportData, defaultCompany, periodLabel);
  };

  const ensureSheetTabActive = async () => {
    if (activeTab !== "SHEET") {
      setActiveTab("SHEET");
      await new Promise((resolve) => setTimeout(resolve, 150));
    }
  };

  const handleDownloadPdf = async () => {
    setIsExporting(true);
    try {
      await ensureSheetTabActive();
      const el = printableRef.current || document.getElementById("gst-printable-report-card");
      if (!el) throw new Error("Printable sheet not rendered");
      await downloadGstReportPdf(el, `GST_Filing_Report_${periodLabel.replace(/\s+/g, "_")}.pdf`);
    } catch (err) {
      alert("Error generating PDF: " + err.message);
    } finally {
      setIsExporting(false);
    }
  };

  const handleSendWhatsApp = async () => {
    setIsExporting(true);
    try {
      await ensureSheetTabActive();
      const el = printableRef.current || document.getElementById("gst-printable-report-card");
      if (!el) throw new Error("Printable sheet not rendered");
      await shareGstReportPdf(el, reportData, defaultCompany, accountantPhone, periodLabel);
    } catch (err) {
      alert("Error sharing to accountant: " + err.message);
    } finally {
      setIsExporting(false);
    }
  };

  const handlePrint = async () => {
    await ensureSheetTabActive();
    window.print();
  };

  if (!isOpen) return null;
  const { summary, tables } = reportData;

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="🏛️ GST Filing Report & Export" size="lg">
      <div className="gst-report-modal" style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
        {/* Statutory Filing Dates Banner */}
        <div style={{ background: "rgba(59, 130, 246, 0.08)", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "8px", padding: "0.5rem 0.75rem", fontSize: "0.78rem", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem" }}>
          <div><span style={{ fontWeight: "700", color: "var(--accent-blue)" }}>Next GSTR-1:</span> {dueDates.gstr1Monthly} (M) / {dueDates.gstr1Qrmp} (QRMP)</div>
          <div><span style={{ fontWeight: "700", color: "var(--accent-blue)" }}>GSTR-3B:</span> {dueDates.gstr3bMonthly} (M) / {dueDates.gstr3bQrmp} (QRMP)</div>
        </div>

        {/* Period Selector Pills */}
        <div style={{ display: "flex", gap: "0.4rem", overflowX: "auto", paddingBottom: "0.2rem" }}>
          {[
            { id: "THIS_MONTH", label: "This Month" },
            { id: "LAST_MONTH", label: "Last Month" },
            { id: "Q1", label: "Q1 (Apr-Jun)" },
            { id: "Q2", label: "Q2 (Jul-Sep)" },
            { id: "Q3", label: "Q3 (Oct-Dec)" },
            { id: "Q4", label: "Q4 (Jan-Mar)" },
            { id: "CUSTOM", label: "Custom Dates" }
          ].map((p) => (
            <button key={p.id} type="button" className={`filter-pill ${preset === p.id ? "active" : ""}`} onClick={() => setPreset(p.id)} style={{ whiteSpace: "nowrap" }}>
              {p.label}
            </button>
          ))}
        </div>

        {preset === "CUSTOM" && (
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <input type="date" className="form-control" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} style={{ flex: 1 }} />
            <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>to</span>
            <input type="date" className="form-control" value={customTo} onChange={(e) => setCustomTo(e.target.value)} style={{ flex: 1 }} />
          </div>
        )}

        {/* 4-KPI Summary Cards */}
        <div className="metrics-row" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "0.5rem" }}>
          <div className="metric-card">
            <div className="metric-label">Sales Turnover</div>
            <div className="metric-value">₹{summary.totalSalesGross.toLocaleString("en-IN")}</div>
            <div style={{ fontSize: "0.65rem", color: "var(--text-dim)" }}>Taxable: ₹{summary.totalSalesTaxable.toLocaleString("en-IN")}</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Output Tax</div>
            <div className="metric-value" style={{ color: "var(--accent-blue)" }}>₹{summary.salesTax.total.toLocaleString("en-IN")}</div>
            <div style={{ fontSize: "0.65rem", color: "var(--text-dim)" }}>C: ₹{summary.salesTax.cgst.toFixed(0)} | S: ₹{summary.salesTax.sgst.toFixed(0)}</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Inward ITC (Purchases)</div>
            <div className="metric-value" style={{ color: "var(--success)" }}>₹{summary.purchasesItc.total.toLocaleString("en-IN")}</div>
            <div style={{ fontSize: "0.65rem", color: "var(--text-dim)" }}>From {summary.counts.purchasesCount} bills</div>
          </div>
          <div className="metric-card" style={{ borderLeft: summary.netPayable.total > 0 ? "3px solid var(--warning)" : "3px solid var(--success)" }}>
            <div className="metric-label">Net Tax to Pay</div>
            <div className="metric-value" style={{ color: summary.netPayable.total > 0 ? "var(--warning)" : "var(--success)" }}>
              ₹{summary.netPayable.total.toLocaleString("en-IN")}
            </div>
            <div style={{ fontSize: "0.65rem", color: "var(--text-dim)" }}>Cash Challan</div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: "flex", gap: "0.4rem", borderBottom: "1px solid var(--border-subtle)", paddingBottom: "0.4rem", overflowX: "auto" }}>
          {[
            { id: "3B", label: "GSTR-3B Summary" },
            { id: "SHEET", label: "📄 Audit Report Sheet" },
            { id: "B2B", label: `B2B Sales (${tables.b2bInvoices.length})` },
            { id: "B2C", label: `B2C Sales (${tables.b2cSummary.length})` },
            { id: "HSN", label: `HSN Items (${tables.hsnSummary.length})` },
            { id: "PURCHASE", label: `Purchase ITC (${tables.purchaseRegister.length})` }
          ].map((tab) => (
            <button key={tab.id} type="button" className={`filter-pill ${activeTab === tab.id ? "active" : ""}`} onClick={() => setActiveTab(tab.id)} style={{ whiteSpace: "nowrap" }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content Panes (with visible, properly-laid-out printable sheet) */}
        <GstReportTabPanes
          activeTab={activeTab}
          summary={summary}
          tables={tables}
          printableRef={printableRef}
          reportData={reportData}
          company={defaultCompany}
          periodLabel={periodLabel}
        />

        {/* Accountant WhatsApp Phone Input */}
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <label style={{ fontSize: "0.8rem", color: "var(--text-muted)", whiteSpace: "nowrap" }}>Accountant WhatsApp:</label>
          <input type="tel" className="form-control" placeholder="e.g. 9876543210" value={accountantPhone} onChange={handlePhoneChange} style={{ flex: 1, padding: "0.4rem 0.6rem", fontSize: "0.85rem" }} />
        </div>

        {/* Action Controls Footer */}
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", justifyContent: "flex-end", marginTop: "0.4rem" }}>
          <Button variant="secondary" onClick={handleDownloadCsv} style={{ fontSize: "0.8rem" }}>📊 Download CSV (Excel)</Button>
          <Button variant="secondary" onClick={handlePrint} style={{ fontSize: "0.8rem" }}>🖨️ Print Sheet</Button>
          <Button variant="secondary" onClick={handleDownloadPdf} disabled={isExporting} style={{ fontSize: "0.8rem" }}>{isExporting ? "Generating..." : "📄 Download PDF"}</Button>
          <Button variant="primary" onClick={handleSendWhatsApp} disabled={isExporting} style={{ fontSize: "0.8rem" }}>{isExporting ? "Sharing..." : "📲 Send to Accountant"}</Button>
        </div>
      </div>
    </BottomSheet>
  );
}
